import { BUSINESS_TIMEZONE, DATE_LOCALE } from "@/constants/dates";

/**
 * Date rendering for the finance and accounting surfaces. One place, so a date reads the same on a
 * blotter row, a detail page and a CSV export.
 *
 * Takes either a plain `YYYY-MM-DD` (a DATE column — accounting dates, settlement dates) or a full
 * timestamp. A date-only string is parsed as UTC noon rather than midnight: `new Date("2026-07-14")`
 * is midnight UTC, which in any negative-offset timezone renders as the 13th.
 */
function toDate(iso: string): Date {
  return /^\d{4}-\d{2}-\d{2}$/.test(iso) ? new Date(`${iso}T12:00:00Z`) : new Date(iso);
}

/**
 * 07/14/2026 — zero-padded and four-digit, stated explicitly rather than left to the locale's
 * default, which gives 7/14/2026 and won't line up in a column.
 *
 * `timeZone` (IANA, e.g. "America/New_York") picks the zone the calendar day is read in. Omitted, the
 * runtime's local zone is used. Pass it for timestamps that must match a server-side day filter.
 */
export function formatDate(iso: string | null | undefined, timeZone?: string): string {
  if (!iso) return "—";
  return toDate(iso).toLocaleDateString(DATE_LOCALE, {
    timeZone,
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });
}

/** Jul 14, 2026 — for places where an unambiguous month reads better than digits. */
export function formatDateLong(iso: string | null | undefined): string {
  if (!iso) return "—";
  return toDate(iso).toLocaleDateString(DATE_LOCALE, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/** 07/14/2026, 3:04 PM. Composed rather than dateStyle:"short", which gives a 2-digit year. */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  return toDate(iso).toLocaleString(DATE_LOCALE, {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/**
 * Whole days from now until `iso`, rounded up — negative once it has passed.
 *
 * Deliberately measured from the instant, not from midnight: "in 3 days" beside an expiry date is a
 * sense of urgency, not an accounting date, and rounding up keeps a lot expiring later today at 1
 * rather than 0.
 */
export function daysUntil(iso: string): number {
  return Math.ceil((toDate(iso).getTime() - Date.now()) / 86_400_000);
}

const BUSINESS_DAY = new Intl.DateTimeFormat("en-CA", {
  timeZone: BUSINESS_TIMEZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

/**
 * Today's calendar day in BUSINESS_TIMEZONE, as a local Date at noon — for date-window helpers that
 * read a Date's local year/month/day, so a report's presets land on the days the API reads them in.
 */
export function businessToday(at: Date = new Date()): Date {
  const [year, month, day] = BUSINESS_DAY.format(at).split("-").map(Number);
  return new Date(year as number, (month as number) - 1, day as number, 12);
}
